/**
 * Money arithmetic — all operations on branded INR/USD/Money values go through
 * `dnum` so we never lose precision to JS floats.
 *
 * Mental model:
 *   • App code passes around branded `number` values (INR, USD).
 *   • Any operation that touches more than display lifts to a Dnum tuple
 *     `[bigint, decimals]`, computes exactly, then lifts back via the brand
 *     constructor.
 *   • `_unsafe*` constructors are reserved for hot loops (chart renderers) —
 *     they trust upstream validation. Everything else uses the shape-checking
 *     constructors from `@/types/money`.
 *
 * dnum API used:
 *   - `dn.from(value, decimals?)`  → construct
 *   - `dn.add` / `subtract` / `multiply` / `divide` / `abs`
 *   - `dn.greaterThan` / `lessThan` / `equal`
 *   - `dn.toNumber(value, decimals?)` → JS number (lossy past 15 sig figs)
 *
 * The default *internal* precision is 8 decimals — well past what any
 * currency needs (paise = 2dp, satoshi = 8dp). Display precision is set
 * by formatters in `@/lib/formatters/money`.
 */

import * as dn from 'dnum';
import type { Dnum } from 'dnum';

import { type INR, _unsafeInr, _unsafeUsd, type USD } from '@/types/money';

/** Internal precision used by every arithmetic operation. */
export const MONEY_INTERNAL_DECIMALS = 8;

/** Lift a branded JS number into the precise Dnum representation. */
const toDnum = (value: number): Dnum => dn.from(value, MONEY_INTERNAL_DECIMALS);

/** Round-trip back to JS number after computation. */
const fromDnum = (value: Dnum): number => dn.toNumber(value, MONEY_INTERNAL_DECIMALS);

/* ============================================================================
 * Operations — generic over money brand. Callers cast to the specific brand
 * via the named helpers below (addInr, subUsd, …).
 * ============================================================================ */

const op2 = (a: number, b: number, f: (x: Dnum, y: Dnum) => Dnum): number =>
  fromDnum(f(toDnum(a), toDnum(b)));

const op1 = (a: number, f: (x: Dnum) => Dnum): number => fromDnum(f(toDnum(a)));

/* ---- INR ----------------------------------------------------------------- */

export const addInr = (a: INR, b: INR): INR => _unsafeInr(op2(a, b, dn.add));
export const subInr = (a: INR, b: INR): INR => _unsafeInr(op2(a, b, dn.subtract));
export const mulInr = (a: INR, factor: number): INR => _unsafeInr(op2(a, factor, dn.multiply));
export const divInr = (a: INR, divisor: number): INR => {
  if (divisor === 0) {
    throw new RangeError('divInr: division by zero');
  }
  return _unsafeInr(op2(a, divisor, dn.divide));
};
export const absInr = (a: INR): INR => _unsafeInr(op1(a, dn.abs));
export const negInr = (a: INR): INR => _unsafeInr(-a as number);

export const sumInr = (values: readonly INR[]): INR => {
  let acc = toDnum(0);
  for (const v of values) {
    acc = dn.add(acc, toDnum(v));
  }
  return _unsafeInr(fromDnum(acc));
};

export const meanInr = (values: readonly INR[]): INR => {
  if (values.length === 0) {
    return _unsafeInr(0);
  }
  return divInr(sumInr(values), values.length);
};

export const minInr = (values: readonly INR[]): INR => {
  const first = values[0];
  if (first === undefined) {
    throw new RangeError('minInr: empty array');
  }
  let acc = first;
  for (let i = 1; i < values.length; i++) {
    const v = values[i];
    if (v !== undefined && v < acc) {
      acc = v;
    }
  }
  return acc;
};

export const maxInr = (values: readonly INR[]): INR => {
  const first = values[0];
  if (first === undefined) {
    throw new RangeError('maxInr: empty array');
  }
  let acc = first;
  for (let i = 1; i < values.length; i++) {
    const v = values[i];
    if (v !== undefined && v > acc) {
      acc = v;
    }
  }
  return acc;
};

/* ---- USD ----------------------------------------------------------------- */

export const addUsd = (a: USD, b: USD): USD => _unsafeUsd(op2(a, b, dn.add));
export const subUsd = (a: USD, b: USD): USD => _unsafeUsd(op2(a, b, dn.subtract));
export const mulUsd = (a: USD, factor: number): USD => _unsafeUsd(op2(a, factor, dn.multiply));
export const divUsd = (a: USD, divisor: number): USD => {
  if (divisor === 0) {
    throw new RangeError('divUsd: division by zero');
  }
  return _unsafeUsd(op2(a, divisor, dn.divide));
};
export const absUsd = (a: USD): USD => _unsafeUsd(op1(a, dn.abs));

export const sumUsd = (values: readonly USD[]): USD => {
  let acc = toDnum(0);
  for (const v of values) {
    acc = dn.add(acc, toDnum(v));
  }
  return _unsafeUsd(fromDnum(acc));
};

/* ---- FX conversion ------------------------------------------------------- */

/**
 * Convert USD → INR at a given spot rate (rupees per dollar). Both inputs
 * lift to Dnum so the conversion is exact in fixed-point space.
 */
export const usdToInr = (usdAmount: USD, inrPerUsd: number): INR => {
  if (!Number.isFinite(inrPerUsd) || inrPerUsd <= 0) {
    throw new RangeError(`usdToInr: invalid spot rate ${inrPerUsd}`);
  }
  return _unsafeInr(op2(usdAmount, inrPerUsd, dn.multiply));
};

/** Convert INR → USD at a given spot rate (rupees per dollar). */
export const inrToUsd = (inrAmount: INR, inrPerUsd: number): USD => {
  if (!Number.isFinite(inrPerUsd) || inrPerUsd <= 0) {
    throw new RangeError(`inrToUsd: invalid spot rate ${inrPerUsd}`);
  }
  return _unsafeUsd(op2(inrAmount, inrPerUsd, dn.divide));
};

/* ---- Comparison ----------------------------------------------------------
 * Signatures accept `number` so callers can pass `INR`, `USD`, or `Money<C>` —
 * all of which structurally extend `number`. The brand layer prevents
 * cross-unit *assignment* at the type level; comparison is unit-blind by
 * design (operators are responsible for comparing same-currency values).
 * --------------------------------------------------------------------------- */

export const eqMoney = (a: number, b: number): boolean =>
  dn.equal(toDnum(a), toDnum(b));

export const gtMoney = (a: number, b: number): boolean =>
  dn.greaterThan(toDnum(a), toDnum(b));

export const ltMoney = (a: number, b: number): boolean =>
  dn.lessThan(toDnum(a), toDnum(b));

/* ---- Sign / direction ---------------------------------------------------- */

export type Sign = 'long' | 'short' | 'zero';

export const signOf = (value: number): Sign => {
  if (value > 0) {
    return 'long';
  }
  if (value < 0) {
    return 'short';
  }
  return 'zero';
};

/* ---- Conversions to lakh / crore (storage stays in rupees) -------------- */

export const LAKH = 100_000;
export const CRORE = 10_000_000;

export const inrToLakh = (value: INR): number => fromDnum(dn.divide(toDnum(value), toDnum(LAKH)));
export const inrToCrore = (value: INR): number => fromDnum(dn.divide(toDnum(value), toDnum(CRORE)));
export const lakhToInr = (value: number): INR => _unsafeInr(op2(value, LAKH, dn.multiply));
export const croreToInr = (value: number): INR => _unsafeInr(op2(value, CRORE, dn.multiply));
